import { JSX, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useDispatch } from 'react-redux';
import {
  Box,
  Button,
  Collapse,
  Skeleton,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import EditIcon from '@mui/icons-material/Edit';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { getSurveySummary } from 'src/api/dataVault/survey';
import { SummaryActionBar } from 'src/components/NewRenterInterface/Survey/SummaryActionBar';
import { QITAnswerRow } from 'src/components/NewRenterInterface/DataVault/components/QITAnswerRow';
import type {
  SurveySummarySection,
  SurveySummaryGroup,
  SurveySummaryAnswer,
} from 'src/api/dataVault/survey';
import type { QITAnswer } from 'src/api/dataVault/sectionAnswers';
import { interviewActions } from 'src/redux/slices/Interview/interviewSlice';

interface Props {
  surveyId: number;
  renterActive: boolean;
  reAnswerBasePath?: string;
  onLoad?: (loading: boolean) => void;
  onSections?: (sections: SurveySummarySection[]) => void;
  onEdit?: (editing: boolean) => void;
}

function groupKey(sectionId: string, group: SurveySummaryGroup): string {
  return `${sectionId}::${group.groupId}::${group.answerGroupId ?? 's'}`;
}

// Unique selection key per group instance. Multi-instance groups key on
// answerGroupId; single (non-multi) groups key on groupId so their selections
// don't collide with each other through the shared "-1" bucket.
function selectionKey(group: SurveySummaryGroup): string {
  return group.answerGroupId != null
    ? `ag:${group.answerGroupId}`
    : `bare:${group.groupId}`;
}

function getGroupPosition(
  section: SurveySummarySection,
  group: SurveySummaryGroup
): number {
  const sameGroup = section.groups.filter((g) => g.groupId === group.groupId);
  return sameGroup.indexOf(group) + 1;
}

function toQITAnswer(a: SurveySummaryAnswer): QITAnswer {
  return {
    qitId: a.qitId,
    label: a.label,
    questionType: a.questionType,
    displayValue: a.displayValue,
    lastUpdatedAt: a.lastUpdatedAt,
    isStale: false,
    numberOfFilesRequired: a.numberOfFilesRequired,
    files: a.files,
    answeredAt: a.lastUpdatedAt,
  };
}

export default function SurveySummary({
  surveyId,
  renterActive,
  reAnswerBasePath,
  onLoad,
  onSections,
  onEdit,
}: Props): JSX.Element {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [sections, setSections] = useState<SurveySummarySection[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [expandAll, setExpandAll] = useState(true);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(
    new Set()
  );
  const [selectedAnswers, setSelectedAnswers] = useState<
    Record<string, number[]>
  >({});

  useEffect(() => {
    onLoad?.(true);
    getSurveySummary(surveyId)
      .then((data) => {
        setSections(data);
        onSections?.(data);
      })
      .catch(() => {
        setSections([]);
        onSections?.([]);
      })
      .finally(() => {
        setLoading(false);
        onLoad?.(false);
      });
  }, [surveyId, onLoad, onSections]);

  const toggleEdit = useCallback(() => {
    setEditing((prev) => {
      const next = !prev;
      onEdit?.(next);
      return next;
    });
    setSelectedAnswers({});
  }, [onEdit]);

  const cancelEdit = useCallback(() => {
    setEditing(false);
    onEdit?.(false);
    setSelectedAnswers({});
  }, [onEdit]);

  const toggleExpandAll = useCallback(() => {
    if (expandAll) {
      const allKeys = new Set<string>();
      for (const s of sections) {
        for (const g of s.groups) {
          allKeys.add(groupKey(s.id, g));
        }
      }
      setCollapsedGroups(allKeys);
    } else {
      setCollapsedGroups(new Set());
    }
    setExpandAll((prev) => !prev);
  }, [expandAll, sections]);

  const toggleGroupCollapse = useCallback((key: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const toggleAnswerSelection = useCallback((key: string, qitId: number) => {
    setSelectedAnswers((prev) => {
      const next = { ...prev };
      const current = next[key] ?? [];
      if (current.includes(qitId)) {
        next[key] = current.filter((id) => id !== qitId);
      } else {
        next[key] = [...current, qitId];
      }
      if (next[key]?.length === 0) {
        delete next[key];
      }
      return next;
    });
  }, []);

  const selectAllInGroup = useCallback((group: SurveySummaryGroup) => {
    const key = selectionKey(group);
    const allQitIds = group.answers.map((a) => a.qitId);
    setSelectedAnswers((prev) => {
      const next = { ...prev };
      next[key] = allQitIds;
      return next;
    });
  }, []);

  const deselectAllInGroup = useCallback((group: SurveySummaryGroup) => {
    const key = selectionKey(group);
    setSelectedAnswers((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const handleReAnswer = useCallback(() => {
    const agPairs: string[] = [];
    const bareQits: number[] = [];
    for (const [key, qitIds] of Object.entries(selectedAnswers)) {
      if (key.startsWith('ag:')) {
        agPairs.push(`${key.slice(3)}:${qitIds.join('-')}`);
      } else {
        bareQits.push(...qitIds);
      }
    }
    const parts: string[] = [];
    if (agPairs.length > 0) {
      parts.push(`answer_group_qits=${agPairs.join(',')}`);
    }
    if (bareQits.length > 0) {
      parts.push(`qits=${bareQits.join(',')}`);
    }
    const qs = parts.join('&');
    const base = reAnswerBasePath ?? `/renter/survey/${surveyId}/answer`;
    // Suppress the file-upload gate so the re-answer session lands directly
    // on the selected questions — including file questions — instead of
    // bouncing through the file-upload screen first.
    dispatch(interviewActions.deferFileUpload());
    navigate(`${base}${qs ? `?${qs}` : ''}`);
  }, [selectedAnswers, surveyId, navigate, reAnswerBasePath, dispatch]);

  const selectedCount = useMemo(() => {
    let count = 0;
    for (const qitIds of Object.values(selectedAnswers)) {
      count += qitIds.length;
    }
    return count;
  }, [selectedAnswers]);

  if (loading) {
    return (
      <Box sx={{ maxWidth: 480, mx: 'auto', width: '100%', px: 2, pt: 2 }}>
        {[0, 1, 2].map((i) => (
          <Box key={i} sx={{ mb: 2 }}>
            <Skeleton variant="text" width={120} sx={{ fontSize: 12, mb: 1 }} />
            <Box
              sx={{
                bgcolor: 'background.paper',
                borderRadius: '16px',
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 2,
                  py: 1.5,
                  borderBottom: '3px solid',
                  borderColor: 'divider',
                }}
              >
                <Skeleton variant="circular" width={8} height={8} />
                <Skeleton variant="text" width="60%" sx={{ fontSize: 14 }} />
              </Box>
              <Box sx={{ px: 2, py: 1 }}>
                {[0, 1].map((j) => (
                  <Box
                    key={j}
                    sx={{
                      py: 1.5,
                      borderBottom: j === 0 ? '1px solid' : 'none',
                      borderColor: 'divider',
                    }}
                  >
                    <Skeleton
                      variant="text"
                      width="40%"
                      sx={{ fontSize: 12, mb: 0.5 }}
                    />
                    <Skeleton
                      variant="text"
                      width="30%"
                      sx={{ fontSize: 15 }}
                    />
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    );
  }

  const hasContent = sections.some((section) => section.groups.length > 0);

  return (
    <Box
      sx={{
        maxWidth: 480,
        mx: 'auto',
        width: '100%',
      }}
    >
      {!hasContent && (
        <Box sx={{ px: 2, pt: 4, textAlign: 'center' }}>
          <Typography
            sx={{ fontSize: 15, color: 'text.secondary', fontWeight: 500 }}
          >
            No answers to review yet.
          </Typography>
          <Typography sx={{ fontSize: 13, color: 'text.disabled', mt: 0.5 }}>
            Complete the questionnaire to see your answers here.
          </Typography>
        </Box>
      )}

      {hasContent && (
        <>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              px: 2,
              py: 1,
              position: 'sticky',
              top: 0,
              zIndex: 2,
              bgcolor: 'background.default',
              pointerEvents: 'auto',
              borderBottom: `2px solid ${theme.palette.divider}`,
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            }}
          >
            <Button
              onClick={toggleExpandAll}
              variant="outlined"
              size="small"
              color="grey"
              sx={{
                fontSize: 14,
                color: 'text.secondary',
                fontWeight: 500,
                textTransform: 'none',
                width: 130,
                justifyContent: 'flex-start',
                gap: 0.5,
              }}
              startIcon={
                expandAll ? (
                  <UnfoldLessIcon sx={{ fontSize: 12 }} />
                ) : (
                  <UnfoldMoreIcon sx={{ fontSize: 12 }} />
                )
              }
            >
              <span>{expandAll ? 'Collapse all' : 'Expand all'}</span>
            </Button>
            {renterActive && !editing && (
              <Button
                variant="contained"
                size="small"
                onClick={toggleEdit}
                sx={{
                  bgcolor: 'secondary.main',
                  color: theme.palette.common.white,
                  fontSize: 13,
                  fontWeight: 500,
                  textTransform: 'none',
                  borderRadius: '8px',
                  px: 1.5,
                  '&:hover': { bgcolor: 'secondary.dark' },
                  boxShadow: 'none',
                }}
                startIcon={<EditIcon sx={{ fontSize: 14 }} />}
              >
                <span>Edit answers</span>
              </Button>
            )}
          </Box>

          {/* Selection banner */}
          {editing && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                bgcolor: alpha(theme.palette.warning.main, 0.12),
                borderBottom: `1px solid ${theme.palette.warning.main}`,
                px: 2,
                py: 1.25,
              }}
            >
              <InfoOutlinedIcon
                sx={{ fontSize: 16, color: theme.palette.warning.dark }}
              />
              <Typography
                sx={{
                  color: theme.palette.text.primary,
                  fontSize: 13,
                  fontWeight: 500,
                }}
              >
                Select the answers that need to be corrected
              </Typography>
            </Box>
          )}

          {/* Sections */}
          <Box sx={{ px: 2, pb: 2 }}>
            {sections
              .filter((section) => section.groups.length > 0)
              .map((section) => (
                <Box key={section.id}>
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: 'text.disabled',
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      pt: 2,
                      pb: 1,
                    }}
                  >
                    {section.name}
                  </Typography>
                  {section.groups.map((group) => {
                    const key = groupKey(section.id, group);
                    const isCollapsed = collapsedGroups.has(key);
                    const title =
                      group.groupName +
                      (group.isMulti
                        ? ` #${getGroupPosition(section, group)}`
                        : '');
                    const subtitle = group.instanceLabel ?? '';

                    const selKey = selectionKey(group);
                    const allQitIds = group.answers.map((a) => a.qitId);
                    const allSelected =
                      editing &&
                      (selectedAnswers[selKey]?.length ?? 0) ===
                        allQitIds.length;

                    return (
                      <Box
                        key={key}
                        sx={{
                          bgcolor: 'background.paper',
                          borderRadius: '16px',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.10)',
                          border: `1px solid ${theme.palette.divider}`,
                          overflow: 'visible',
                          mb: 1.25,
                        }}
                      >
                        <Box
                          onClick={() => toggleGroupCollapse(key)}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            px: 2,
                            py: 1.5,
                            cursor: 'pointer',
                            bgcolor: 'background.paper',
                            borderBottom: `3px solid ${theme.palette.primary.main}`,
                            borderTopLeftRadius: 16,
                            borderTopRightRadius: 16,
                            position: 'sticky',
                            top: 44,
                            zIndex: 1,
                            pointerEvents: 'auto',
                          }}
                        >
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              bgcolor: 'primary.main',
                              flexShrink: 0,
                            }}
                          />
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography
                              sx={{
                                fontSize: 14,
                                fontWeight: 600,
                                color: 'text.primary',
                              }}
                            >
                              {title}
                            </Typography>
                            {subtitle && (
                              <Typography
                                sx={{
                                  fontSize: 12,
                                  color: 'text.secondary',
                                  mt: 0.25,
                                }}
                              >
                                {subtitle}
                              </Typography>
                            )}
                          </Box>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            {editing && group.answers.length > 1 && (
                              <Button
                                variant="contained"
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (allSelected) {
                                    deselectAllInGroup(group);
                                  } else {
                                    selectAllInGroup(group);
                                  }
                                }}
                                sx={{
                                  fontSize: 14,
                                  fontWeight: 600,
                                  bgcolor: 'primary.main',
                                  color: theme.palette.common.white,
                                  textTransform: 'none',
                                  minWidth: 90,
                                  px: 1.25,
                                  py: 0.25,
                                  borderRadius: '6px',
                                  boxShadow: 'none',
                                  '&:hover': {
                                    boxShadow: 'none',
                                    bgcolor: 'primary.dark',
                                  },
                                }}
                              >
                                {allSelected ? 'Deselect all' : 'Select all'}
                              </Button>
                            )}
                            {isCollapsed ? (
                              <ExpandMoreIcon
                                sx={{ color: 'grey.400', fontSize: 16 }}
                              />
                            ) : (
                              <ExpandLessIcon
                                sx={{ color: 'grey.400', fontSize: 16 }}
                              />
                            )}
                          </Box>
                        </Box>

                        <Collapse in={!isCollapsed}>
                          <Box
                            sx={{
                              overflow: 'hidden',
                              borderBottomLeftRadius: 16,
                              borderBottomRightRadius: 16,
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                '& > *:nth-of-type(even)': {
                                  backgroundColor: theme.palette.action.hover,
                                },
                                '& > *': {
                                  px: 0.5,
                                  borderRadius: 0.5,
                                },
                              }}
                            >
                              {group.answers.map((answer) => {
                                const isSelected =
                                  editing &&
                                  (selectedAnswers[selKey] ?? []).includes(
                                    answer.qitId
                                  );

                                return (
                                  <QITAnswerRow
                                    key={answer.qitId}
                                    qit={{
                                      id: answer.qitId,
                                      label: answer.label,
                                    }}
                                    answer={toQITAnswer(answer)}
                                    selectionMode={editing}
                                    isSelected={isSelected}
                                    onToggleSelect={() =>
                                      toggleAnswerSelection(
                                        selKey,
                                        answer.qitId
                                      )
                                    }
                                    hideBorder
                                  />
                                );
                              })}
                            </Box>
                          </Box>
                        </Collapse>
                      </Box>
                    );
                  })}
                </Box>
              ))}
          </Box>

          {editing && (
            <SummaryActionBar
              selectedCount={selectedCount}
              onSubmit={handleReAnswer}
              onCancel={cancelEdit}
            />
          )}
        </>
      )}
    </Box>
  );
}
